import { useContainer } from "@src/contexts/container-context";
import { useMemo, useState } from "react";

import {
  getLearnerTerm,
  Product,
} from "@transfr-inc/dashboard-components/constants";

import { checkPromisesFulfilled, getPromisesValues } from "@lib/browser-utils";
import { doExportData, ExportStatus } from "@lib/export-utils";
import { Role } from "@models/role";
import { exportOrganizationCeStats } from "@services/organization.utils";

const exploredVrHeader = "Careers Explored";
const instructorsHeader = "Instructors";

const exportOptions = { createFile: false };

const mergeDefaultOptions = {
  includeInstructors: true,
};

const mergeTrekExportData = ({
  trekSummary,
  ceStats,
  namesMap,
  headerTitle,
  options,
}) => {
  const { includeInstructors } = { ...mergeDefaultOptions, ...options };
  const ceStatsByName = new Map(
    ceStats.data.map((values) => [values[headerTitle], values])
  );

  return Object.entries(trekSummary).map(([key, stats]) => {
    const nameValue = namesMap.get(key);
    const ceStatsValue = ceStatsByName.get(nameValue);

    const data = {
      [headerTitle]: nameValue,
      [`Unique ${getLearnerTerm({ plural: true })}`]: stats.totalActiveUsers,
      "Explored VR": ceStatsValue[exploredVrHeader] || "0",
      "Budgets Created": stats.totalBudgetPlansCompleted,
      "Careers Selected": stats.totalPlansSubmitted,
    };

    if (includeInstructors) {
      data["Instructors"] = ceStatsValue[instructorsHeader];
    }

    return data;
  });
};

export const useTrekExport = ({ organizations, classrooms }) => {
  const { trekService, organizationService, classroomService } = useContainer();
  const [exportStatus, setExportStatus] = useState();

  const [isLoading, hasError] = useMemo(
    () => [
      exportStatus === ExportStatus.creating,
      exportStatus === ExportStatus.error,
    ],
    [exportStatus]
  );

  const [organizationsNamesMap, classroomsNamesMap] = useMemo(
    () => [
      new Map(organizations.map((c) => [c.code, c.name])),
      new Map(classrooms.map((c) => [c.classroomId, c.name])),
    ],
    [organizations, classrooms]
  );

  const organizationHeaderTitle = "Organization";
  const classroomHeaderTitle = "Classroom";

  const exportTrekData = async ({
    orgCode,
    classroomId,
    filters,
    prefixFileName,
    role,
    userId,
    isMultiOrg,
  }) => {
    const fileName = `${prefixFileName} - Trek`;
    let csvData;
    setExportStatus(ExportStatus.creating);

    try {
      if (classroomId) {
        csvData = await getClassroomCSVData({ classroomId, orgCode, filters });
      } else if (orgCode && isMultiOrg) {
        csvData = await getMultiOrgCSVData({ orgCode, filters });
      } else {
        csvData = await getSingleOrgCSVData({ orgCode, role, userId, filters });
      }

      await doExportData(fileName, csvData);
      setExportStatus(ExportStatus.complete);
    } catch (error) {
      console.error(error);
      setExportStatus(ExportStatus.error);
    }
  };

  const getMultiOrgCSVData = async ({ orgCode, filters }) => {
    const filtersWithProduct = { productId: Product.TRK, ...filters };
    const exportRequest = organizationService.exportMultiOrganizationCeStats(
      orgCode,
      filtersWithProduct,
      undefined,
      exportOptions
    );

    const [trekSummary, ceStats] = await Promise.allSettled([
      trekService.getInsightsGrouped({
        orgCode,
        filters,
      }),
      exportRequest,
    ])
      .then(checkPromisesFulfilled)
      .then(getPromisesValues);

    return mergeTrekExportData({
      trekSummary,
      ceStats,
      namesMap: organizationsNamesMap,
      headerTitle: organizationHeaderTitle,
      options: { includeInstructors: false },
    });
  };

  const getSingleOrgCSVData = async ({ orgCode, role, userId, filters }) => {
    const filtersWithProduct = { productId: Product.TRK, ...filters };
    const orgCodeOrUserId = role === Role.INSTRUCTOR.id ? userId : orgCode;
    const exportRequest = exportOrganizationCeStats({
      organizationService,
      role,
      orgCodeOrUserId,
      filters: filtersWithProduct,
      fileName: undefined,
      getMultiOrg: false,
      options: exportOptions,
    });

    const [trekSummary, ceStats] = await Promise.allSettled([
      trekService.getInsightsGrouped({
        orgCode,
        filters,
      }),
      exportRequest,
    ])
      .then(checkPromisesFulfilled)
      .then(getPromisesValues);

    return mergeTrekExportData({
      trekSummary,
      ceStats,
      namesMap: classroomsNamesMap,
      headerTitle: classroomHeaderTitle,
    });
  };

  const getClassroomCSVData = async ({ classroomId, orgCode, filters }) => {
    const filtersWithProduct = { productId: Product.TRK, ...filters };
    const exportRequest = classroomService.exportClassroomCeStats(
      classroomId,
      filtersWithProduct,
      undefined,
      exportOptions
    );

    const [trekSummary, ceStats] = await Promise.allSettled([
      trekService.getInsights({
        orgCode,
        filters: { ...filters, classroomIds: [classroomId] },
      }),
      exportRequest,
    ])
      .then(checkPromisesFulfilled)
      .then(getPromisesValues);

    return mergeTrekExportData({
      trekSummary: { [classroomId]: trekSummary },
      ceStats,
      namesMap: classroomsNamesMap,
      headerTitle: classroomHeaderTitle,
    });
  };

  return {
    hasError,
    isLoading,
    exportTrekData,
  };
};
